import React, { forwardRef, useEffect, useImperativeHandle } from "react";

import { Box, Button } from "@mui/material";
import ClipLoader from "react-spinners/ClipLoader";

import {
    BackButton,
    ContentInner,
    Footer,
    FooterInner,
    PrimaryButtonWrap,
    Root,
    ScrollBody,
} from "./WizardStepLayout.styled";
import {
    WizardStepLayoutHandle,
    WizardStepLayoutProps,
} from "./WizardStepLayout.types";

const DEFAULT_PRIMARY_LABELS = {
    next: "Next",
    complete: "Complete",
} as const;

const WizardStepLayout = forwardRef<
    WizardStepLayoutHandle,
    WizardStepLayoutProps
>(
    (
        {
            title,
            descriptionTitle,
            description,
            children,
            onBack,
            showBack,
            backLabel = "Back",
            primaryAction = "next",
            primaryLabel,
            onPrimary,
            primaryDisabled = false,
            primaryTestId,
            backTestId,
            isLoading = false,
            maxWidth = 900,
            renderFooter,
            renderPrimaryExtras,
        },
        ref,
    ) => {
        const shouldShowBack = showBack ?? Boolean(onBack);
        const resolvedPrimaryLabel =
            primaryLabel ?? DEFAULT_PRIMARY_LABELS[primaryAction];
        const hasHeader = Boolean(title || descriptionTitle || description);

        useImperativeHandle(ref, () => ({
            scrollToBottom: (behavior = "smooth") => {
                window.scrollTo({
                    top: document.documentElement.scrollHeight,
                    behavior,
                });
            },
            scrollToTop: (behavior = "smooth") => {
                window.scrollTo({ top: 0, behavior });
            },
        }));

        useEffect(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }, [title, descriptionTitle, description]);

        return (
            <Root>
                <ScrollBody $maxWidth={maxWidth}>
                    <ContentInner>
                        {hasHeader && (
                            <div className="wizard-title-area mb-4">
                                {title ? <h2>{title}</h2> : null}
                                {(descriptionTitle || description) && (
                                    <p>
                                        {descriptionTitle ? (
                                            <strong>{descriptionTitle}</strong>
                                        ) : null}
                                        {description}
                                    </p>
                                )}
                            </div>
                        )}
                        {children}
                    </ContentInner>
                </ScrollBody>

                <Footer>
                    {renderFooter ? (
                        renderFooter()
                    ) : (
                        <FooterInner>
                            {shouldShowBack ? (
                                <BackButton>
                                    <Button
                                        variant="text"
                                        color="primary"
                                        onClick={onBack}
                                        disabled={isLoading}
                                        data-cy={backTestId}
                                    >
                                        {backLabel}
                                    </Button>
                                </BackButton>
                            ) : (
                                <span />
                            )}

                            {renderPrimaryExtras ? (
                                <Box
                                    sx={{
                                        marginLeft: "auto",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    {renderPrimaryExtras()}
                                </Box>
                            ) : null}

                            {onPrimary ? (
                                <PrimaryButtonWrap>
                                    <Button
                                        className="py-2"
                                        variant="contained"
                                        color="primary"
                                        onClick={onPrimary}
                                        disabled={primaryDisabled || isLoading}
                                        data-cy={primaryTestId}
                                    >
                                        {isLoading ? (
                                            <ClipLoader
                                                color="#ffffff"
                                                size={20}
                                                loading={isLoading}
                                            />
                                        ) : (
                                            resolvedPrimaryLabel
                                        )}
                                    </Button>
                                </PrimaryButtonWrap>
                            ) : null}
                        </FooterInner>
                    )}
                </Footer>
            </Root>
        );
    },
);

WizardStepLayout.displayName = "WizardStepLayout";

export default WizardStepLayout;
export type {
    WizardStepLayoutHandle,
    WizardStepLayoutProps,
    WizardStepPrimaryAction,
} from "./WizardStepLayout.types";
